import { PaycloudBranch } from "../types";
import BaseService from "./base-service";
import { RequestDto } from "./dto/request/request-dto";

interface CloverAuthRequest {
  code: string;
  client_id: string;
  merchant_id: string;
  employee_id: string;
}
interface GetBranchesResponse {
  branches: PaycloudBranch[];
}

export type PendingCbuCloverBranch = {
  branchId: number;
  name: string;
  address: string | null;
  status: number;
  cloverExternalId: string | null;
  identityNumber: string | null;
};

interface GetPendingCbuBranchesResponse {
  branches: PendingCbuCloverBranch[];
  totalCount: number;
}

export type AvailableCloverBranch = Pick<PaycloudBranch, "branchId" | "name" | "address">;

interface AvailableBranchesResponse {
  branches: AvailableCloverBranch[];
}

interface ReassignBranchResponse {
  branchId: number;
}

interface BranchesReqDTO {
  limit: number;
  offset: number;
}

export class CloverService extends BaseService {
  private resource = "/clover";

  async authenticate(
    params: CloverAuthRequest,
    onSuccess: () => void,
    onError: (errorMessage: string) => void
  ): Promise<void> {
    const reqParams = new RequestDto(`${this.resource}/auth`, params);
    await this.doPost<void>(reqParams, onSuccess, onError);
  }


  async getPendingBranches(
    params: BranchesReqDTO,
    onSuccess: (data: GetBranchesResponse) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(`/api${this.resource}/pending-branches`, params);
    await this.doGet<GetBranchesResponse>(reqParams, onSuccess, onError);
  }

  async getPendingCbuBranches(
    params: BranchesReqDTO,
    onSuccess: (data: GetPendingCbuBranchesResponse) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(`/api${this.resource}/pending-cbu-branches`, params);
    await this.doGet<GetPendingCbuBranchesResponse>(reqParams, onSuccess, onError);
  }

  async saveBranchCbu(
    branchId: number,
    bankAccountNumber: string,
    onSuccess: (data: { branchId: number; bankAccountNumber: string }) => void,
    onError: (errorMessage: string) => void,
    identityNumber?: string
  ) {
    const reqParams = new RequestDto(`/api${this.resource}/branch-cbu`, {
      BranchId: branchId,
      BankAccountNumber: bankAccountNumber,
      ...(identityNumber ? { IdentityNumber: identityNumber } : {}),
    });
    await this.doPost(reqParams, onSuccess, onError);
  }

  async getAvailableBranches(
    cloverMerchantId: string,
    onSuccess: (data: AvailableBranchesResponse) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(`/api${this.resource}/available-branches`, {
      CloverMerchantId: cloverMerchantId,
    });
    await this.doGet<AvailableBranchesResponse>(reqParams, onSuccess, onError);
  }

  async reassignBranch(
    cloverMerchantId: string,
    branchId: number,
    onSuccess: (data: ReassignBranchResponse) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(`/api${this.resource}/reassign-branch`, {
      CloverMerchantId: cloverMerchantId,
      BranchId: branchId,
    });
    await this.doPatch<ReassignBranchResponse>(reqParams, onSuccess, onError);
  }

  async unlinkBranch(
    branchId: number,
    onSuccess: () => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(`/api${this.resource}/unlink-branch`, {
      BranchId: branchId,
    });
    await this.doPatch<void>(reqParams, onSuccess, onError);
  }

  async updateConsiderPaymentEmployee(
    branchId: number,
    considerPaymentEmployee: boolean,
    onSuccess: (data: { branchId: number | null; considerPaymentEmployee: boolean }) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(
      `/api${this.resource}/consider-payment-employee`,
      {
        BranchId: branchId,
        ConsiderPaymentEmployee: considerPaymentEmployee,
      },
      {
        "Content-Type": "application/json",
      }
    );
    await this.doPatch(reqParams, onSuccess, onError);
  }
}
